/**
 * Validate file is a PowerPoint file
 */
export const isValidPPTFile = (file: File): boolean => {
  const validTypes = [
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ];
  const validExtensions = ['.ppt', '.pptx'];
  
  const hasValidType = validTypes.includes(file.type);
  const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  
  return hasValidType || hasValidExtension;
};

/**
 * Validate file size
 */
export const isValidFileSize = (file: File, maxSizeMB: number = 50): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Validate that at least one task is selected
 */
export const isValidTaskSelection = (tasks: string[]): boolean => {
  return tasks.length > 0;
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Sanitize filename
 */
export const sanitizeFilename = (filename: string): string => {
  return filename.replace(/[^a-zA-Z0-9._-]/g, '_');
};

/**
 * Validate file is a supported style guide format
 */
export const isValidStyleGuideFile = (file: File): boolean => {
  const validExtensions = ['.pdf', '.docx', '.doc', '.txt', '.md', '.png', '.jpg', '.jpeg', '.pptx', '.ppt', '.xlsx', '.xls', '.html', '.htm'];
  return validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
};

/**
 * Validate style guide file size (max 25MB)
 */
export const isValidStyleGuideSize = (file: File, maxSizeMB: number = 25): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Validate attachment file type
 */
export const isValidAttachment = (file: File): boolean => {
  const validTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
  ];
  
  return validTypes.includes(file.type);
};

/**
 * Validate attachment size (max 10MB)
 */
export const isValidAttachmentSize = (file: File, maxSizeMB: number = 10): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

